import PropTypes from "prop-types";
import React, { useCallback, useState, useEffect, useRef } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import cleanupQwotedTopHundredEntry from "./DataCleanup";

const personAnalyticsUrl = "/interact/api/promoter_analytics?profile_id=:profile_id&period=:period";
const prProfilesUrl = `${globalThis.Qwoted?.SUPABASE_API_EXTERNAL_URL || ""}/rest/v1/pr_profiles`;
const SEARCH_DEBOUNCE_MS = 300;

// Default entry for a profile with no analytics history yet (e.g. no pitches sent).
// Mirrors the shape produced by the promoter_analytics endpoint (see schema.js) so it
// slots into the payload the same way a normal entry does.
const createDefaultPersonEntry = (profile) => ({
  bad_behavior_points: 0,
  conversation_outcome_points: 0,
  direct_media_reply_points: 0,
  disabled_interactions_bad_behavior_points: 0,
  hidden: false,
  ineligible: false,
  num_conversations: 0,
  num_direct_media_replies: 0,
  num_pitches: 0,
  num_represented: 0,
  pitch_speed_points: 0,
  profile_id: profile.profile_id,
  profile_slug: profile.slug,
  spam_flagged_count_bad_behavior_points: 0,
  suspected_leaker_dialog_enabled_count_bad_behavior_points: 0,
  total_points: 0,
  v1_profile_id: profile.migrate_id || "",
  // pr_profiles carries user_migrate_id straight through from the same v1 user column the bulk
  // top_prs load reads as v1_user_id (promoter_analytics.sql.erb: `up.user_migrate_id as
  // v1_user_id`) -- passed through unchanged, no format conversion, so a manually added profile's
  // backup-restore remap works the same as a bulk-loaded one.
  v1_user_id: profile.user_migrate_id ?? null,
});

function AddPersonModal({ show, onClose, onAdd, existingProfileIds, listSize = 0 }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("last_quarter");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState("");
  const debounceTimer = useRef(null);
  const abortControllerRef = useRef(null);

  // Search PR profiles by name, then enrich each with its promoter analytics
  // for the selected period (falling back to a zero-score entry when a
  // profile has no analytics row yet).
  const performSearch = useCallback(
    async (query, period) => {
      if (!query.trim()) {
        setResults([]);
        setError(null);
        return;
      }

      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this search
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setLoading(true);
      setError(null);

      try {
        // Search PR profiles from Supabase. The query text is URL-encoded before it's spliced into
        // the ilike pattern -- a raw "&" or "%" in a searched name would otherwise truncate or alter
        // the querystring.
        const profilesResponse = await fetch(`${prProfilesUrl}?name=ilike.*${encodeURIComponent(query)}*&limit=50`, {
          cache: "no-store",
          method: "GET",
          signal,
          headers: new Headers({
            ...authenticatedHeadersHash(),
            "Content-Type": "application/json",
          }),
        });

        if (!profilesResponse.ok) {
          throw new Error(`Failed to search profiles: ${profilesResponse.status}`);
        }

        const profiles = await profilesResponse.json();

        // Enrich each profile with analytics and mark which are already added. A failed analytics
        // fetch (non-ok response, network error, bad JSON) is let through to the outer catch rather
        // than swallowed here -- degrading it to a zero-score row would let an endpoint outage
        // publish fabricated zeros. The zero-score fallback only applies when the request succeeds
        // and simply has no analytics row for that profile.
        const enriched = await Promise.all(
          profiles.map(async (profile) => {
            const isAlreadyAdded = existingProfileIds.includes(profile.profile_id);

            try {
              const analyticsResponse = await fetch(
                personAnalyticsUrl.replace(":profile_id", profile.profile_id).replace(":period", period),
                {
                  cache: "no-store",
                  method: "GET",
                  signal,
                  headers: new Headers(authenticatedHeadersHash()),
                },
              );

              if (!analyticsResponse.ok) {
                throw new Error(`Failed to load analytics: ${analyticsResponse.status}`);
              }

              const analyticsData = await analyticsResponse.json();
              const analytics = analyticsData.data.promoters[0] || null;
              return { profile, analytics, isAlreadyAdded };
            } catch (err) {
              // Let a cancelled request through unchanged so the outer catch's AbortError branch
              // handles it; tag every other failure (bad status, network error, malformed JSON) so
              // the outer catch can tell it apart from a failed profile search below.
              if (err.name === "AbortError") throw err;
              const analyticsError = new Error("Couldn't load analytics — try again");
              analyticsError.name = "AnalyticsFetchError";
              throw analyticsError;
            }
          }),
        );

        setResults(enriched);
      } catch (err) {
        // A cancelled search (superseded by a newer keystroke) must not touch state at all -- doing
        // so could publish a stale search's results after a newer search has already started, or
        // flip loading off while that newer search is still in flight.
        if (err.name === "AbortError") {
          return;
        }
        setError(err.name === "AnalyticsFetchError" ? err.message : `Failed to search: ${err.message}`);
        setResults([]);
      } finally {
        // Skip the loading flip on an aborted request too: this callback belongs to the superseded
        // search, and only the latest (non-aborted) request may update state.
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [existingProfileIds],
  );

  // Debounced search on query change
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(searchQuery, selectedPeriod);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, selectedPeriod, performSearch]);

  // Close modal and reset state
  const handleClose = () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setSearchQuery("");
    setResults([]);
    setError(null);
    setPosition("");
    onClose();
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (!show) return null;

  // Builds the payload entry for a search result and hands it to the parent
  const handleAdd = (result) => {
    const payloadEntry = result.analytics
      ? { ...cleanupQwotedTopHundredEntry(result.analytics), hidden: !!result.analytics.ineligible }
      : createDefaultPersonEntry(result.profile);

    const positionValue = position ? parseInt(position, 10) : undefined;
    onAdd(payloadEntry, positionValue);
    handleClose();
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Person to List</h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-md-8">
                <div className="mb-3">
                  <label htmlFor="period-select" className="form-label">
                    Period
                  </label>
                  <select
                    id="period-select"
                    className="form-select"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                  >
                    <option value="this_quarter">This Quarter</option>
                    <option value="last_quarter">Last Quarter</option>
                    <option value="all_time">All Time</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label htmlFor="position-input" className="form-label">
                    Position
                  </label>
                  <input
                    id="position-input"
                    type="number"
                    className="form-control"
                    placeholder="Optional"
                    min="1"
                    max={listSize + 1}
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                  />
                  <small className="text-muted">1–{listSize + 1} (leave empty to add at end)</small>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="search-input" className="form-label">
                Search by Name
              </label>
              <input
                id="search-input"
                type="text"
                className="form-control"
                placeholder="Enter person's name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {loading && <small className="text-muted">Searching...</small>}
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {results.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead className="thead-light">
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Pitches</th>
                      <th>Total Points</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result) => (
                      <tr key={result.profile.profile_id}>
                        <td>{result.profile.name}</td>
                        <td>{result.profile.company_name}</td>
                        <td>{result.analytics ? result.analytics.num_pitches : 0}</td>
                        <td>{result.analytics ? result.analytics.total_points : 0}</td>
                        <td>
                          <button
                            type="button"
                            className={`btn btn-sm ${result.isAlreadyAdded ? "btn-secondary" : "btn-primary"}`}
                            onClick={() => handleAdd(result)}
                            disabled={result.isAlreadyAdded}
                          >
                            {result.isAlreadyAdded ? "Already Added" : "Add"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && results.length === 0 && searchQuery && (
              <div className="alert alert-info" role="alert">
                No people found matching your search.
              </div>
            )}

            {!loading && results.length === 0 && !searchQuery && (
              <div className="text-muted text-center py-3">Enter a search term to find people.</div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

AddPersonModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingProfileIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  listSize: PropTypes.number,
};

export default AddPersonModal;
